import React, { useState } from "react";
import {
  MoreVertical,
  Edit2,
  Trash2,
  Share2,
  FileText,
  Users,
  Calendar,
  ChevronRight,
} from "lucide-react";
import useFinance from "../../../hooks/useFinance";
import PermissionBadge from "../Common/PermissionBadge";
import EditTableModal from "../Modals/EditTableModal";
import DeleteTableModal from "../Modals/DeleteTableModal";
import ShareTableModal from "../Modals/ShareTableModal";

export default function TableCard({ table, onClick, onRefresh }) {
  const { updateTable, deleteTable } = useFinance();
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // 서버에서 권한을 체크하므로, 프론트엔드에서는 모든 작업을 시도할 수 있음
  const canEdit = true;
  const canDelete = true;
  const canShare = true;

  const handleEdit = async (data) => {
    const result = await updateTable({
      table_id: table.id,
      ...data,
    });

    if (result.ok) {
      window.showToast?.("테이블이 수정되었습니다.", "success");
      onRefresh();
      setShowEditModal(false);
    } else if (result.denied) {
      window.showToast?.("테이블 수정 권한이 없습니다.", "error");
    } else {
      window.showToast?.("테이블 수정에 실패했습니다.", "error");
    }
  };

  const handleDelete = async () => {
    const result = await deleteTable(table.id);

    if (result.ok) {
      window.showToast?.("테이블이 삭제되었습니다.", "success");
      onRefresh();
      setShowDeleteModal(false);
    } else if (result.denied) {
      window.showToast?.("테이블 삭제 권한이 없습니다.", "error");
    } else {
      window.showToast?.("테이블 삭제에 실패했습니다.", "error");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <div
        className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
        onClick={onClick}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors flex items-center gap-2">
                {table.table_name}
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                {table.description || "설명이 없습니다"}
              </p>
            </div>

            {(canEdit || canDelete || canShare) && (
              <div className="relative ml-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-4 h-4 text-gray-500" />
                </button>

                {showMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(false);
                      }}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      {canEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEditModal(true);
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm text-gray-700"
                        >
                          <Edit2 className="w-4 h-4" />
                          테이블 수정
                        </button>
                      )}

                      {canShare && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowShareModal(true);
                            setShowMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm text-gray-700"
                        >
                          <Share2 className="w-4 h-4" />
                          공유 관리
                        </button>
                      )}

                      {canDelete && (
                        <>
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteModal(true);
                              setShowMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 text-sm text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            테이블 삭제
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {table.column_count}개 컬럼
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {table.row_count}개 행
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <PermissionBadge type="table" level={table.permission_level} />
              <span className="text-xs text-gray-500">{table.owner_name}</span>
            </div>

            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Calendar className="w-3 h-3" />
              {formatDate(table.updated_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditTableModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEdit}
          table={table}
        />
      )}

      {showDeleteModal && (
        <DeleteTableModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          table={table}
        />
      )}

      {showShareModal && (
        <ShareTableModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          tableId={table.id}
          tableName={table.table_name}
          onUpdate={onRefresh}
        />
      )}
    </>
  );
}
